import { collection, query, where, orderBy, limit, getDocs, Timestamp } from "firebase/firestore";
import { firestore } from "_utils/firebase-app";

// session cache 
// Caches Firestore results for date ranges that do NOT include today.
// Past ranges are immutable — the same query will always return the same data,
// so there is no reason to hit Firestore more than once per session.
// Today's ranges are never cached because new events may arrive at any time.
//
// Eviction: simple FIFO cap at MAX_ENTRIES. The cache is cleared automatically
// on page refresh (module-level variable, not persisted).

const _cache    = new Map();
const MAX_ENTRIES = 40;

function todayStart() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

// A range is cacheable only when the end date is strictly before today.
function isCacheable(endDate) {
    return endDate < todayStart();
}

function cacheKey(...parts) {
    return parts.join('|');
}

function cacheGet(key) {
    return _cache.get(key) ?? null;
}

function cacheSet(key, value) {
    if (_cache.size >= MAX_ENTRIES) {
        // Evict the oldest entry (Map preserves insertion order)
        _cache.delete(_cache.keys().next().value);
    }
    _cache.set(key, value);
}

// Exported so components can bust the cache if needed (e.g. after a manual refresh)
export function clearEventsCache() {
    _cache.clear();
}

// Fetch location_fixed events only — used by Coverage heatmap
export async function fetchDeviceEvents(tenantId, appName, devEui, startDate, endDate) {
    const key = cacheKey('loc', tenantId, appName, devEui, startDate.toISOString(), endDate.toISOString());
    if (isCacheable(endDate)) {
        const hit = cacheGet(key);
        if (hit) return hit;
    }

    const eventsRef = collection(
        firestore, "tenants", tenantId, "applications", appName, "devices", devEui, "events"
    );
    const q = query(
        eventsRef,
        where("type", "in", ["location_fixed", "location_fixed_gps_limit"]),
        where("at", ">=", Timestamp.fromDate(startDate)),
        where("at", "<=", Timestamp.fromDate(endDate)),
        orderBy("at", "asc"),
        limit(1000)
    );
    const snap   = await getDocs(q);
    const result = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (isCacheable(endDate)) cacheSet(key, result);
    return result;
}

// Fetch location_fixed events for a time window — used by Trail tab
export async function fetchTrailEvents(tenantId, appName, devEui, startDate, endDate) {
    const key = cacheKey('trail', tenantId, appName, devEui, startDate.toISOString(), endDate.toISOString());
    if (isCacheable(endDate)) {
        const hit = cacheGet(key);
        if (hit) return hit;
    }

    const eventsRef = collection(
        firestore, "tenants", tenantId, "applications", appName, "devices", devEui, "events"
    );
    const q = query(
        eventsRef,
        where("type", "in", ["location_fixed", "location_fixed_gps_limit"]),
        where("at", ">=", Timestamp.fromDate(startDate)),
        where("at", "<=", Timestamp.fromDate(endDate)),
        orderBy("at", "asc"),
        limit(500)
    );
    const snap   = await getDocs(q);
    const result = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (isCacheable(endDate)) cacheSet(key, result);
    return result;
}

// Fetch ALL event types for a device in a date range — used by Inspect tab
export async function fetchAllDeviceEvents(tenantId, appName, devEui, startDate, endDate) {

    const eventsRef = collection(
        firestore, "tenants", tenantId, "applications", appName, "devices", devEui, "events"
    );
    const q = query(
        eventsRef,
        where("at", ">=", Timestamp.fromDate(startDate)),
        where("at", "<=", Timestamp.fromDate(endDate)),
        orderBy("at", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Fetch all events for a device in ascending time order — used by Idle Report
// and Movement Heatmap. Includes both 'event' (fPort 8 movement notifications)
// and 'location_fixed' (fPort 2 BLE/GPS positions).
export async function fetchDeviceEventsAsc(tenantId, appName, devEui, startDate, endDate) {
    const key = cacheKey('asc', tenantId, appName, devEui, startDate.toISOString(), endDate.toISOString());
    if (isCacheable(endDate)) {
        const hit = cacheGet(key);
        if (hit) return hit;
    }

    const eventsRef = collection(
        firestore, "tenants", tenantId, "applications", appName, "devices", devEui, "events"
    );
    const q = query(
        eventsRef,
        where("at", ">=", Timestamp.fromDate(startDate)),
        where("at", "<=", Timestamp.fromDate(endDate)),
        orderBy("at", "asc"),
        limit(3000)
    );
    const snap   = await getDocs(q);
    const result = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (isCacheable(endDate)) cacheSet(key, result);
    return result;
}

// Fetch events across multiple devices in parallel — used by the Idle Report
// which aggregates idle periods across the whole fleet.
// Returns a flat map: { [devEui]: events[] }
export async function fetchEventsForDevices(devices, startDate, endDate) {

    const results = await Promise.all(
        devices.map(async (device) => {
            const tenantId = device.meta?.tenantId;
            const appName  = device.meta?.applicationName;
            const devEui   = device.id;
            if (!tenantId || !appName || !devEui) return { devEui, events: [] };
            try {
                const events = await fetchDeviceEventsAsc(tenantId, appName, devEui, startDate, endDate);
                return { devEui, events };
            } catch (e) {
                console.error(`Failed to fetch events for device ${devEui}:`, e);
                return { devEui, events: [] };
            }
        })
    );

    const map = {};
    results.forEach(({ devEui, events }) => { map[devEui] = events; });
    return map;
}
